import { Text } from '@mantine/core'
import React from 'react'

import { useTranslation } from '@flecto/translations'
import { type DesignSystemColors } from '@flecto/ui/lib/theme'

import { useInsuranceManagerContext } from '../../../provider'
import { getFormattedDate } from '../../../utils/getFormattedStatusDate'

export default function useStatusLabel(until?: string) {
  const { insurances } = useInsuranceManagerContext()
  const {
    t,
    i18n: { language },
  } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })

  const colors = new Map<string, DesignSystemColors>([
    ['ready', 'neutral500'],
    ['expired', 'neutral500'],
    ['pending', 'neutral500'],
    ['active', 'primary700'],
  ])

  let labelColor = colors.get(insurances.meta?.metaStatus ?? 'ready')

  const label = React.useMemo(() => {
    if (insurances.isClaimPeriod) return t('status.insurance_within_claim')

    if (insurances.meta?.metaStatus === 'ready') {
      return t('status.insurance_not_active')
    }

    if (insurances.meta?.metaStatus === 'pending') {
      return t('status.insurance_pending')
    }

    if (insurances.meta?.metaStatus === 'active' && insurances?.meta?.until) {
      return t('status.insurance_active', {
        replace: { date: getFormattedDate(insurances.meta?.until) },
      })
    }

    return insurances.meta?.metaStatus
      ? t(`status.${insurances.meta?.metaStatus}`)
      : ''
  }, [insurances, language])

  if (insurances.isClaimPeriod) {
    labelColor = 'blue600'
  }

  return (
    <Text size='sm' color={labelColor} data-testid='status'>
      {label}
    </Text>
  )
}
